import { IActionFn, IPageData } from '@/store/types';
import { $http, $asset, $tool } from '@/axios';
import { hasAuth } from '@/utils/validate';
import { P_ITEM_NAV, P_SEC_PROD_NAV } from '@/menu/auth-const';
import { IModuleState } from './types';
import { navName } from './util';
import store from '@/store';

// 查询导航树
export const queryMatNavTree: IActionFn<IModuleState> = ({ commit, state }, orgCode) => {
  const ENTITY_MODEL = 'Model';
  const ENTITY_MATERIAL = 'Material';
  const ENTITY_SECTION = 'SectionProduct';

  const requests = [];
  const hasMatNavAuth = hasAuth([P_ITEM_NAV]);      // 是否拥有物料导航权限
  const hasSecNavAuth = hasAuth([P_SEC_PROD_NAV]);  // 是否拥有型材产品导航权限

  if (hasMatNavAuth) {
    // 物品导航
    const modName = orgCode ? `${ENTITY_MODEL}_${orgCode}` : ENTITY_MODEL;
    const fetchModNav = $http.get(`/v1/baseinfo/nav/${modName}/detail`);

    // 材料导航
    const matName = orgCode ? `${ENTITY_MATERIAL}_${orgCode}` : ENTITY_MATERIAL;
    const fetchMatNav = $http.get(`/v1/baseinfo/nav/${matName}/detail`);

    requests.push(fetchModNav, fetchMatNav);
  }

  if (hasSecNavAuth) {
    // 型材产品导航
    const secName = orgCode ? `${ENTITY_SECTION}_${orgCode}` : ENTITY_SECTION;
    const fetchSecNav = $http.get(`/v1/baseinfo/nav/${secName}/detail`);
    requests.push(fetchSecNav);
  }

  commit('setMatNavTree', { ...state.tree, loading: true });
  Promise.all(requests).then((results) => {
    const rawList = [];
    const modTree = results.filter((item: any) => item?.entityType === ENTITY_MODEL)[0] || null;
    const matTree = results.filter((item: any) => item?.entityType === ENTITY_MATERIAL)[0] || null;
    const secTree = results.filter((item: any) => item?.entityType === ENTITY_SECTION)[0] || null;

    if (hasMatNavAuth) {
      const modRoot = { id: -1, entityType: ENTITY_MODEL, ...modTree, name: '物品' };
      const matRoot = { id: -2, entityType: ENTITY_MATERIAL, ...matTree, name: '材料' };
      rawList.push(modRoot, matRoot);
    }

    if (hasSecNavAuth) {
      const secRoot = { id: -3, entityType: ENTITY_SECTION, ...secTree, name: '型材产品' };
      rawList.push(secRoot);
    }

    commit('setMatNavTree', { raw: rawList });
  }).catch(() => {
    commit('setMatNavTree', { data: [] });
  });
};

export const queryMatImgList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setMatImgList', { ...state.images, loading: true });
    const res: IPageData = await $http.post('/v1/baseinfo/nav/preview', payload);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setMatImgList', { params: payload, data, pageNum, pageSize, total });
  } catch (e) {
    commit('setMatImgList', { data: [], pageNum: 0, pageSize: 0, total: 0, params: null });
  }
};

// 当前编辑的导航树相关
// ==========================================================================================================
// 查询已发布导航树详情(预留, 已经在 queryVersionList 里调用过此接口)
// export const queryPublishTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
//   try {
//     commit('setPublishTree', { ...state.pubTrees, loading: true });
//     const res = await $asset('oss', `data/navigation_${navName(payload)}.json`, { ignoreCodes: [404] });
//     commit('setPublishTree', { raw: res ? [res] : [] });
//   } catch (e) {
//     commit('setPublishTree', { data: [] });
//   }
// };

// 发布正在编辑的导航树
export const publishNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/publish', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 覆盖正在编辑的导航树
export const reeditNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/reedit', { name: navName(payload) });
    commit('setExpanded', { expand: false }); // 收起历史版本列表
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除正在编辑的导航树
export const deleteNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/delete', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除已发布导航树
export const deletePublishTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/drop', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 版本相关
// ==========================================================================================================
// 查询导航树版本列表(包括'已发布导航'和'导航版本列表')
export const queryVersionList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  const name = navName(payload);
  const fetchPublishTree = $asset('oss', `data/navigation_${name}.json`, { ignoreCodes: [404] });
  const fetchVersionList = $http.post('/v1/baseinfo/nav/versionlist', { name });

  // 新版
  // commit('setVersionList', { ...state.pubTrees, loading: true });
  // commit('setVersionList', { ...state.versions, loading: true });
  // Promise.all([fetchPublishTree, fetchVersionList]).then((results) => {
  //   const [publishTree = null, versionList = []] = results || [];
  //   commit('setVersionList', { raw: versionList }); // 更新已发布的导航
  //   commit('setPublishTree', { raw: publishTree ? [publishTree] : [] }); // 更新版本列表
  // }).catch(() => {
  //   commit('setMatNavTree', { data: [] });
  // });

  // 旧版
  Promise.all([fetchPublishTree, fetchVersionList]).then((results) => {
    const [publishTree = null, versionList = []] = results || [];

    // 如果'已发布的导航'存在, 则将导航名称加入到版本列表的第一个位置
    if (publishTree) {
      commit('setPublishTree', { raw: [publishTree] }); // 更新已发布导航树 store
      commit('setVersionList', { raw: [name, ...(versionList as string[])] });
    } else {
      commit('setVersionList', { raw: versionList });
    }
  }).catch(() => {
    commit('setMatNavTree', { data: [] });
  });
};

// 查询对应版本的导航树详情
export const queryVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setVersionTree', { data: [], loading: true });
    const res = await $http.get(`/v1/baseinfo/nav/${navName(payload)}/versiondetail`);
    commit('setVersionTree', { raw: res ? [res] : [] });
  } catch (e) {
    commit('setVersionTree', { data: [] });
  }
};

// 发布导航树版本
export const publishVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/publishversion', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除导航版本
export const deleteVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/dropversion', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 保存导航版本
export const saveVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', { type: 'save-version', loading: true });
    await $http.post('/v1/baseinfo/nav/saveversion', { name: navName(payload) });
    store.commit('common/setToast', { visible: true, text: '版本保存成功' });
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 节点相关
// ==========================================================================================================
// 新增导航节点
export const addNavNode: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/baseinfo/nav/addnode', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 编辑导航节点
export const editNavNode: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', { type: 'save-node', loading: true });
    // await $http.post('/navigation/ex/node/modify', payload);
    await $http.post('/v1/baseinfo/nav/node/modify', payload);
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 删除导航节点
export const deleteNavNode: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post('/navigation/ex/node/delete', payload);
    await $http.post('/v1/baseinfo/nav/node/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 导航节点排序
export const sortNavNode: IActionFn<IModuleState> = async (context, payload) => {
  // await $http.post('/navigation/ex/node/sort', payload);
  await $http.post('/v1/baseinfo/nav/node/sort', payload);
};

// 保存导航节点配置
export const saveNavConfig: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', { type: 'save-setting', loading: true });
    // await $http.post('/navigation/ex/node/config/save', payload);
    await $http.post('/v1/baseinfo/nav/node/saveconfig', payload);
    store.commit('common/setToast', { visible: true, text: '保存成功' });
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 字典相关
// ==========================================================================================================
// 查询所有过滤项(实体分类和标签分类)
export const queryAllFilters: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setMatCateTree', { ...state.cate, loading: true });
    commit('setMatLabelList', { ...state.label, loading: true });
    // const res: any[] = await $http.get('/baseinfo/filter/all');
    const res: any[] = await $http.get('/v1/baseinfo/filter/listbycommand');
    const category = (res || []).filter((item) => item.isTree);
    const labels = (res || []).filter((item) => !item.isTree);
    commit('setMatCateTree', { raw: category });
    commit('setMatLabelList', { raw: labels });
  } catch (e) {
    commit('setMatCateTree', { data: [] });
    commit('setMatLabelList', { data: [] });
  }
};

// 查询颜色列表
export const queryColors: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setMatColors', { ...state.colors, loading: true });
    const res: { table: any[] } = await $tool('color').post('/color-table');
    commit('setMatColors', { data: res.table });
  } catch (e) {
    commit('setMatColors', { data: [] });
  }
};

// 查询实体类型字典
export const queryEntityTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setEntityTypes', { ...state.entityTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/FilterItemEntityType');
    commit('setEntityTypes', { data: res });
  } catch (e) {
    commit('setEntityTypes', { data: [] });
  }
};
